import React, { useState, useMemo } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, ScrollView } from 'react-native';
import { IActivity, KeyStage } from '@eduwrite/shared';
import { useStrokeStore } from '../store/useStrokeStore';
import { SkiaCanvas } from './SkiaCanvas';
import { useStageTokens } from '../hooks/useStageTokens';
import { getStudentSafeActivityTitle } from '../utils/studentTaskTitle';

interface SentenceBuildActivityProps {
    activity: IActivity;
    onComplete: () => void;
    keyStage?: KeyStage;
}

function shuffle<T>(arr: T[]): T[] {
    const copy = [...arr];
    for (let i = copy.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [copy[i], copy[j]] = [copy[j], copy[i]];
    }
    return copy;
}

export const SentenceBuildActivity: React.FC<SentenceBuildActivityProps> = ({ activity, onComplete, keyStage }) => {
    const sentence: string = activity.config?.sentence || '';
    const hint: string = activity.config?.hint || '';
    const { setStructuredData } = useStrokeStore.getState();
    const tokens = useStageTokens(keyStage);

    // Words with stable index so we can track which were used
    const words = useMemo(() => shuffle(sentence.split(' ')), [sentence]);
    const [builtWords, setBuiltWords] = useState<string[]>([]);
    const [usedIndices, setUsedIndices] = useState<Set<number>>(new Set());

    const handleWordTap = (word: string, idx: number) => {
        if (usedIndices.has(idx)) return;
        setBuiltWords(prev => [...prev, word]);
        setUsedIndices(prev => new Set([...prev, idx]));
    };

    const handleRemoveLast = () => {
        if (builtWords.length === 0) return;
        // Re-enable the last used index
        // We need to track which idx corresponds to which built word position
        setBuiltWords(prev => prev.slice(0, -1));
        setUsedIndices(prev => {
            const arr = [...prev];
            arr.pop();
            return new Set(arr);
        });
    };

    const builtSentence = builtWords.join(' ');
    const expectedSentence = sentence;
    const isComplete = builtWords.length === words.length;
    const isCorrect = builtSentence.toLowerCase() === expectedSentence.toLowerCase();

    const handleSubmit = () => {
        setStructuredData({ builtSentence, expected: expectedSentence, correct: isCorrect });
        onComplete();
    };

    return (
        <ScrollView
            style={styles.container}
            contentContainerStyle={styles.scrollContent}
            showsVerticalScrollIndicator={true}
            bounces={false}
        >
            {/* Header */}
            <View style={styles.header}>
                <View style={styles.headerRow}>
                    <Text style={[styles.title, { fontSize: tokens.titleFontSize }]} allowFontScaling={true} maxFontSizeMultiplier={1.5}>{getStudentSafeActivityTitle(activity)}</Text>
                </View>
                {activity.instructions && (
                    <Text style={[styles.instructions, { fontSize: tokens.bodyFontSize }]} allowFontScaling={true} maxFontSizeMultiplier={1.5}>{activity.instructions}</Text>
                )}
                {hint ? <Text style={styles.hint} allowFontScaling={true} maxFontSizeMultiplier={1.5}>💡 {hint}</Text> : null}
            </View>

            {/* Drop zone — built sentence */}
            <View testID="sentence-drop-zone" style={styles.dropZone}>
                {builtWords.length === 0 ? (
                    <Text style={styles.dropPlaceholder} allowFontScaling={true} maxFontSizeMultiplier={1.5}>Tap words below to build your sentence...</Text>
                ) : (
                    <ScrollView horizontal showsHorizontalScrollIndicator={false}>
                        <View style={styles.builtRow}>
                            {builtWords.map((w, i) => (
                                <View key={i} style={styles.builtWord}>
                                    <Text style={styles.builtWordText} allowFontScaling={true} maxFontSizeMultiplier={1.5}>{w}</Text>
                                </View>
                            ))}
                        </View>
                    </ScrollView>
                )}
            </View>

            {/* Feedback */}
            {isComplete && (
                <View style={[styles.feedback, isCorrect ? styles.feedbackCorrect : styles.feedbackWrong]}>
                    <Text style={styles.feedbackText} allowFontScaling={true} maxFontSizeMultiplier={1.5}>
                        {isCorrect ? '✓ Perfect sentence!' : `✗ Try again! Expected: "${expectedSentence}"`}
                    </Text>
                </View>
            )}

            {/* Word tiles */}
            <View style={styles.tilesArea}>
                {words.map((word, i) => (
                    <TouchableOpacity
                        key={i}
                        style={[styles.wordTile, usedIndices.has(i) && styles.wordTileUsed]}
                        onPress={() => handleWordTap(word, i)}
                        disabled={usedIndices.has(i)}
                        activeOpacity={0.7}
                        accessibilityRole="button"
                        accessibilityLabel={`Word ${word}`}
                        accessibilityState={{ disabled: usedIndices.has(i) }}
                    >
                        <Text style={[styles.wordTileText, usedIndices.has(i) && styles.wordTileTextUsed]} allowFontScaling={true} maxFontSizeMultiplier={1.5}>
                            {word}
                        </Text>
                    </TouchableOpacity>
                ))}
            </View>

            {/* Controls */}
            <View style={styles.controls}>
                <TouchableOpacity style={styles.undoBtn} onPress={handleRemoveLast} activeOpacity={0.7}
                    accessibilityRole="button"
                    accessibilityLabel="Undo"
                    accessibilityHint="Removes the last word you added"
                >
                    <Text style={styles.undoTxt} allowFontScaling={true} maxFontSizeMultiplier={1.5}>⌫ Undo</Text>
                </TouchableOpacity>
                <TouchableOpacity
                    style={[styles.submitBtn, !isComplete && styles.disabledBtn]}
                    onPress={handleSubmit}
                    disabled={!isComplete}
                    activeOpacity={0.7}
                    accessibilityRole="button"
                    accessibilityLabel="Done"
                    accessibilityHint="Submits your sentence"
                    accessibilityState={{ disabled: !isComplete }}
                >
                    <Text style={styles.submitTxt} allowFontScaling={true} maxFontSizeMultiplier={1.5}>DONE! ➔</Text>
                </TouchableOpacity>
            </View>

            {/* Teacher Feedback Overlay */}
            <View style={StyleSheet.absoluteFill} pointerEvents="none">
                <SkiaCanvas readOnly={true} strokes={[]} />
            </View>
        </ScrollView>
    );
};

const styles = StyleSheet.create({
    container: { flex: 1, width: '100%', minHeight: 0, backgroundColor: '#fff' },
    scrollContent: { flexGrow: 1, padding: 16, justifyContent: 'space-around' },
    header: { marginBottom: 12 },
    headerRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 4 },
    title: { fontSize: 22, fontWeight: 'bold', color: '#1E293B' },
    instructions: { fontSize: 14, color: '#64748B', fontStyle: 'italic', marginBottom: 2 },
    hint: { fontSize: 13, color: '#7C3AED', marginTop: 2 },
    dropZone: {
        minHeight: 64, backgroundColor: '#F8FAFC', borderRadius: 12,
        borderWidth: 2, borderColor: '#CBD5E1', borderStyle: 'dashed',
        padding: 10, marginVertical: 8, justifyContent: 'center',
    },
    dropPlaceholder: { color: '#94A3B8', fontStyle: 'italic', fontSize: 14 },
    builtRow: { flexDirection: 'row', gap: 8, alignItems: 'center' },
    builtWord: {
        backgroundColor: '#EEF2FF', borderRadius: 8,
        paddingHorizontal: 12, paddingVertical: 6,
        borderWidth: 1, borderColor: '#C7D2FE',
    },
    builtWordText: { fontSize: 16, fontWeight: '700', color: '#3730A3' },
    feedback: {
        alignSelf: 'stretch', paddingHorizontal: 14, paddingVertical: 8,
        borderRadius: 12, marginVertical: 6,
    },
    feedbackCorrect: { backgroundColor: '#D1FAE5' },
    feedbackWrong: { backgroundColor: '#FEE2E2' },
    feedbackText: { fontWeight: 'bold', fontSize: 14, color: '#1E293B' },
    tilesArea: { flexDirection: 'row', flexWrap: 'wrap', gap: 8, justifyContent: 'center', marginVertical: 12 },
    wordTile: {
        paddingHorizontal: 14, paddingVertical: 10, minHeight: 48,
        backgroundColor: '#F0FDF4', borderRadius: 12,
        borderWidth: 1.5, borderColor: '#86EFAC',
        shadowColor: '#000', shadowOffset: { width: 0, height: 1 },
        shadowOpacity: 0.05, shadowRadius: 2, elevation: 1,
    },
    wordTileUsed: { backgroundColor: '#F1F5F9', borderColor: '#CBD5E1', opacity: 0.4 },
    wordTileText: { fontSize: 16, fontWeight: '700', color: '#166534' },
    wordTileTextUsed: { color: '#94A3B8' },
    controls: { flexDirection: 'row', gap: 10, marginTop: 8 },
    undoBtn: {
        flex: 1, height: 56, minHeight: 52, backgroundColor: '#FEF2F2', borderRadius: 14,
        alignItems: 'center', justifyContent: 'center', borderWidth: 1, borderColor: '#FECACA',
    },
    undoTxt: { fontSize: 15, fontWeight: 'bold', color: '#EF4444' },
    submitBtn: {
        flex: 3,
        height: 56,
        minHeight: 52,
        backgroundColor: '#22c55e',
        borderRadius: 14,
        alignItems: 'center',
        justifyContent: 'center',
        shadowColor: '#22c55e',
        shadowOffset: { width: 0, height: 3 },
        shadowOpacity: 0.3,
        shadowRadius: 6,
        elevation: 4,
    },
    disabledBtn: { backgroundColor: '#D1D5DB' },
    submitTxt: { fontSize: 18, fontWeight: '900', color: '#fff', letterSpacing: 0.5 },
});
